import { Button, Checkbox, Group, Paper, Select, TextInput, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorPage } from './page.error';

import { useQueriesWithGlobal } from '@data-access';
import {
  CreateOrganisationDto,
  GetValuesParams,
  organisationsQueries,
  useCreateOrganisation,
  useUpdateOrganisation,
  useValues,
} from '@organisations/data-access';
import { FullMainHeight, TsrButtonLink } from '@ui';

export function ManageOrganisationPage() {
  const { t } = useTranslation();
  const { organisationId } = useParams({ strict: false });
  const navigate = useNavigate();
  const [mode] = useState<'create' | 'edit'>(
    organisationId ? 'edit' : 'create',
  );

  const form = useForm<CreateOrganisationDto>({
    initialValues: {
      name: '',
      role: '',
      parent: '',
      country: '',
      project_name: 'Tournesol dérobé',
      is_tc_sms: false,
    },
  });

  const queries = useQueriesWithGlobal(organisationsQueries);
  const organisation = useQuery({
    ...queries.detail(Number(organisationId)),
    enabled: !!organisationId,
  });
  const values = useValues({
    params: {
      role: form.getValues().role,
      org: form.getValues().parent,
    },
  });
  const createOrganisation = useCreateOrganisation();
  const updateOrganisation = useUpdateOrganisation();

  const handleOnSubmit = (v: CreateOrganisationDto) => {
    if (mode === 'create') {
      createOrganisation.mutate(v, {
        onSuccess: () => {
          navigate({ to: '/organisations' });
        },
      });
    }

    if (mode === 'edit' && Object.keys(v).length) {
      updateOrganisation.mutate(
        {
          id: Number(organisationId),
          data: v,
        },
        {
          onSuccess: () => {
            navigate({ to: '/organisations' });
          },
        },
      );
    }
  };

  useEffect(() => {
    if (organisation.data) {
      form.setInitialValues({
        ...form.getValues(),
        name: organisation.data.name,
        parent: organisation.data.parent?.id.toString(),
        role: organisation.data.role.id.toString(),
        country: organisation.data.country,
        project_name: organisation.data.project_name,
        is_tc_sms: organisation.data.is_tc_sms,
      });
      form.reset();
    }
  }, [organisation.data]);
  return (
    <FullMainHeight.Root>
      <FullMainHeight.Header>
        <Paper>
          <Title order={2}>
            {mode === 'create'
              ? t('organisations.add_title')
              : t('organisations.edit_title')}
          </Title>
        </Paper>
      </FullMainHeight.Header>
      <FullMainHeight.FlexContent>
        <FullMainHeight.Sidebar>
          <Paper h="100%">
            <form onSubmit={form.onSubmit(handleOnSubmit)}>
              <TextInput label="Nom" required {...form.getInputProps('name')} />
              <Select
                label="Role"
                mt="sm"
                required
                {...form.getInputProps('role')}
                data={values.data?.role}
              />
              <Select
                label="N+1"
                mt="sm"
                {...form.getInputProps('parent')}
                data={values.data?.organization}
                disabled={values.data?.organization.length === 0}
              />
              <Select
                data={values.data?.countries}
                label={t('forms.countries_label')}
                mt="sm"
                required
                {...form.getInputProps('country')}
              />
              <TextInput
                label={t('organisations.project_name')}
                mt="sm"
                required
                {...form.getInputProps('project_name')}
              />
              <Checkbox
                label={t('organisations.is_tc_sms')}
                mt="sm"
                {...form.getInputProps('is_tc_sms', { type: 'checkbox' })}
              />
              <Group mt="md">
                <Button type="submit">{t('common.save')}</Button>
                <TsrButtonLink color="red" to="/organisations">
                  {t('common.cancel')}
                </TsrButtonLink>
              </Group>
            </form>
          </Paper>
        </FullMainHeight.Sidebar>
      </FullMainHeight.FlexContent>
    </FullMainHeight.Root>
  );
}

ManageOrganisationPage.Error = ErrorPage;

export default ManageOrganisationPage;
